import { System } from '../../../engine/ecs/core/System';
import { TransformComponent } from '../../../engine/ecs/components/TransformComponent';
import { SpriteComponent } from '../../../engine/ecs/components/SpriteComponent';
import { Vector2 } from '../../../engine/ecs/core/Vector2';

/**
 * Environmental Animation System for Pacman-Antarctica
 * Demonstrates time-based environmental animations within ECS architecture
 *
 * Features:
 * - Continuous falling snow animation
 * - Wall melting effects over time
 * - Wind effects on environmental elements
 * - Arctic sparkle animations
 */
export class EnvironmentalAnimationSystem extends System {
  private canvas: HTMLCanvasElement;
  private context: CanvasRenderingContext2D;
  private time: number = 0;

  // Environmental animation data
  private fallingSnow: Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    size: number;
    rotation: number;
    rotationSpeed: number;
    life: number;
    maxLife: number;
    type: 'snowflake' | 'ice_particle' | 'arctic_crystal';
  }> = [];

  private wallMeltingEffects: Map<
    number,
    {
      meltProgress: number;
      dripPoints: Array<{ x: number; y: number; size: number; life: number }>;
      sparkles: Array<{
        x: number;
        y: number;
        life: number;
        intensity: number;
      }>;
    }
  > = new Map();

  private windEffect: {
    strength: number;
    direction: number;
    time: number;
  } = {
    strength: 0.5,
    direction: 0,
    time: 0,
  };

  constructor(canvas: HTMLCanvasElement) {
    super();
    this.canvas = canvas;
    this.context = canvas.getContext('2d')!;
    this.registerRequiredComponent(TransformComponent);
    this.registerRequiredComponent(SpriteComponent);
  }

  public update(deltaTime: number): void {
    // Update global time for animations
    this.time += deltaTime;

    // Update wind effect
    this.updateWindEffect(deltaTime);

    // Update falling snow
    this.updateFallingSnow(deltaTime);

    // Update wall melting effects
    this.updateWallMeltingEffects(deltaTime);

    // Spawn new environmental elements
    this.spawnEnvironmentalElements(deltaTime);

    // Render environmental effects
    this.renderEnvironmentalEffects();
  }

  /**
   * Update wind effect - demonstrates time-based environmental animation
   */
  private updateWindEffect(deltaTime: number): void {
    this.windEffect.time += deltaTime;

    // Create gentle wind pattern using sine wave
    this.windEffect.strength = 0.3 + Math.sin(this.windEffect.time * 0.5) * 0.2;
    this.windEffect.direction = Math.sin(this.windEffect.time * 0.3) * 0.1;
  }

  /**
   * Update falling snow - demonstrates continuous background animation
   */
  private updateFallingSnow(deltaTime: number): void {
    for (let i = this.fallingSnow.length - 1; i >= 0; i--) {
      const snow = this.fallingSnow[i];

      // Apply wind effect
      snow.vx +=
        this.windEffect.strength * this.windEffect.direction * deltaTime;
      snow.vx *= 0.99; // Water resistance

      // Update position
      snow.x += snow.vx * deltaTime * 30;
      snow.y += snow.vy * deltaTime * 15; // speed of the snow falling from the top
      snow.rotation += snow.rotationSpeed * deltaTime;

      // Reduce life
      snow.life -= deltaTime;

      // Remove snow that's off-screen or expired
      if (snow.y > this.canvas.height + 50 || snow.life <= 0) {
        this.fallingSnow.splice(i, 1);
      }
    }
  }

  /**
   * Update wall melting effects - demonstrates environmental animation over time
   */
  private updateWallMeltingEffects(deltaTime: number): void {
    for (const [entityId, effect] of this.wallMeltingEffects) {
      // Progress melting over time
      effect.meltProgress += deltaTime * 0.1; // Slow melting

      // Update drip points
      for (let i = effect.dripPoints.length - 1; i >= 0; i--) {
        const drip = effect.dripPoints[i];
        drip.y += deltaTime * 100; // Drips fall
        drip.life -= deltaTime;

        if (drip.life <= 0) {
          effect.dripPoints.splice(i, 1);
        }
      }

      // Update sparkles
      for (let i = effect.sparkles.length - 1; i >= 0; i--) {
        const sparkle = effect.sparkles[i];
        sparkle.life -= deltaTime;
        sparkle.intensity =
          Math.sin(this.time * 5 + sparkle.x * 0.1) * 0.5 + 0.5;

        if (sparkle.life <= 0) {
          effect.sparkles.splice(i, 1);
        }
      }

      // Add new drip points occasionally
      if (Math.random() < 0.02) {
        // 2% chance per frame
        effect.dripPoints.push({
          x: Math.random() * 24 - 12,
          y: -12,
          size: 1 + Math.random() * 2,
          life: 2 + Math.random() * 5, // Longer life (increased from 2-5 to 2-20 seconds)
        });
      }

      // Add new sparkles occasionally
      if (Math.random() < 0.01) {
        // 1% chance per frame
        effect.sparkles.push({
          x: Math.random() * 24 - 12,
          y: Math.random() * 24 - 12,
          life: 1 + Math.random() * 2,
          intensity: Math.random(),
        });
      }
    }
  }

  /**
   * Spawn new environmental elements - demonstrates continuous animation generation
   */
  private spawnEnvironmentalElements(deltaTime: number): void {
    // Spawn falling snow continuously
    if (Math.random() < 0.1) {
      // 10% chance per frame
      const type = ['snowflake', 'ice_particle', 'arctic_crystal'][
        Math.floor(Math.random() * 3)
      ] as 'snowflake' | 'ice_particle' | 'arctic_crystal';

      this.fallingSnow.push({
        x: Math.random() * this.canvas.width,
        y: -20 - Math.random() * 50,
        vx: (Math.random() - 0.5) * 2,
        vy: 30 + Math.random() * 20,
        size:
          type === 'snowflake'
            ? 4 + Math.random() * 4
            : type === 'ice_particle'
            ? 6 + Math.random() * 8
            : 8 + Math.random() * 6,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 2,
        life: 10 + Math.random() * 10,
        maxLife: 20,
        type: type,
      });
    }
  }

  /**
   * Initialize wall melting effect for a wall entity
   */
  public initializeWallMelting(entityId: number): void {
    this.wallMeltingEffects.set(entityId, {
      meltProgress: 0,
      dripPoints: [],
      sparkles: [],
    });
  }

  /**
   * Initialize wall erosion effect for a wall entity (Arctic theme)
   */
  public initializeWallErosion(entityId: number): void {
    this.wallMeltingEffects.set(entityId, {
      meltProgress: 0,
      dripPoints: [],
      sparkles: [],
    });
  }

  /**
   * Render all environmental effects
   */
  private renderEnvironmentalEffects(): void {
    this.context.save();

    // Render falling snow
    this.renderFallingSnow();

    // Render wall melting effects
    this.renderWallMeltingEffects();

    this.context.restore();
  }

  /**
   * Render falling snow with different types
   */
  private renderFallingSnow(): void {
    for (const snow of this.fallingSnow) {
      this.context.save();
      this.context.translate(snow.x, snow.y);
      this.context.rotate(snow.rotation);
      this.context.globalAlpha = snow.life / snow.maxLife;

      switch (snow.type) {
        case 'snowflake':
          this.renderSnowflake(snow.size);
          break;
        case 'ice_particle':
          this.renderIceParticle(snow.size);
          break;
        case 'arctic_crystal':
          this.renderArcticCrystal(snow.size);
          break;
      }

      this.context.restore();
    }
  }

  /**
   * Render snowflake pattern
   */
  private renderSnowflake(size: number): void {
    this.context.fillStyle = '#FFFFFF';
    this.context.beginPath();
    this.context.arc(0, 0, size / 2, 0, Math.PI * 2);
    this.context.fill();
  }

  /**
   * Render ice particle
   */
  private renderIceParticle(size: number): void {
    this.context.fillStyle = '#F0F8FF';
    this.context.beginPath();
    this.context.arc(0, 0, size / 2, 0, Math.PI * 2);
    this.context.fill();
  }

  /**
   * Render arctic crystal
   */
  private renderArcticCrystal(size: number): void {
    const grad = this.context.createRadialGradient(0, 0, 0, 0, 0, size / 2);
    grad.addColorStop(0, '#00CED1');
    grad.addColorStop(0.7, '#F0F8FF');
    grad.addColorStop(1, '#B0E0E6');

    this.context.fillStyle = grad;
    this.context.beginPath();
    this.context.arc(0, 0, size / 2, 0, Math.PI * 2);
    this.context.fill();
  }

  /**
   * Render wall melting effects
   */
  private renderWallMeltingEffects(): void {
    for (const [entityId, effect] of this.wallMeltingEffects) {
      // Render drip points
      for (const drip of effect.dripPoints) {
        this.context.save();
        this.context.globalAlpha = (drip.life / 5) * 0.6; // More subtle
        this.context.fillStyle = '#E0F6FF';
        this.context.strokeStyle = '#E0F6FF';

        this.context.lineWidth = drip.size * 0.8;
        this.context.beginPath();
        this.context.moveTo(drip.x, drip.y - drip.size * 1.5);
        this.context.lineTo(drip.x, drip.y + drip.size * 1.5);
        this.context.stroke();
        this.context.restore();
      }

      // Render sparkles
      for (const sparkle of effect.sparkles) {
        this.context.save();
        this.context.globalAlpha = (sparkle.life / 3) * sparkle.intensity;
        this.context.fillStyle = '#FFFFFF';
        this.context.shadowColor = '#B3E5FC';
        this.context.shadowBlur = 8;
        this.context.beginPath();
        this.context.arc(sparkle.x, sparkle.y, 2, 0, Math.PI * 2);
        this.context.fill();
        this.context.restore();
      }
    }
  }

  /**
   * Get environmental animation data for rendering system
   */
  public getEnvironmentalData(): {
    fallingSnow: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      rotation: number;
      rotationSpeed: number;
      life: number;
      maxLife: number;
      type: 'snowflake' | 'ice_particle' | 'arctic_crystal';
    }>;
    wallMeltingEffects: Map<
      number,
      {
        meltProgress: number;
        dripPoints: Array<{ x: number; y: number; size: number; life: number }>;
        sparkles: Array<{
          x: number;
          y: number;
          life: number;
          intensity: number;
        }>;
      }
    >;
    windEffect: {
      strength: number;
      direction: number;
      time: number;
    };
  } {
    return {
      fallingSnow: this.fallingSnow,
      wallMeltingEffects: this.wallMeltingEffects,
      windEffect: this.windEffect,
    };
  }
}
